import { Button, Dropdown, DropdownItem, Icon, SlideOver } from 'lynkco-up-core';
import { useEffect, useState } from 'react';
import { LoadingMask } from '../../components';
import { formatDateOnly } from '../../parts/shared/utils';
import { ColabQuery, GetColab } from './types';
import { getColabs } from './getColab';
import { ColabDetails, ColabStatistics } from '../../parts/Colab';
import ColabFilters from '../../parts/Colab/colabFilters';
import dayjs from 'dayjs';
import ColabStatusCell from '../../parts/Colab/colabStatusCell';
import { convertToExcel } from '../../parts/Colab/convertToExcel';
import { useColabContext } from '../../parts/Colab/ColabContext';
import ColabCategory from '../../parts/Colab/colabCategory';
import ColabCategoryCombobox from '../../parts/Colab/colabCategoryCombobox';
import { getCategoryOptions, recordStatuses, suggestColabCategory } from '../../parts/Colab/colabOptions';
import usePanelResize from '../../hooks/usePanelResize';
import ColabCheckbox from '../../parts/Colab/colabCheckbox';
import ColabModelQuickFilter, {
  allModelQuickFilter,
  getCarModelLabel,
  getCarModelQuickFilterValue,
} from '../../parts/Colab/colabModelQuickFilter';
import '../../parts/Colab/colabBulkToolbar.css';

const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
const defaultStart = dayjs().subtract(100, 'day').startOf('day').format(dateFormat);
const defaultEnd = dayjs().endOf('day').format(dateFormat);
let updatedStartDate = '';
let updatedEndDate = '';

function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

type ClientFilters = {
  vehicleModel: string[];
  status: string[];
  category: string[];
  source: string[];
  market: string[];
  minAge: string;
  maxAge: string;
  searchText: string;
};

const defaultClientFilters: ClientFilters = {
  vehicleModel: [],
  status: [],
  category: [],
  source: [],
  market: [],
  minAge: '',
  maxAge: '',
  searchText: '',
};

function ColabsTable() {
  const [isLoading, setIsLoading] = useState(false);
  const { data, setData, updateStatus, updateCategory } = useColabContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [detailsIsOpen, setDetailsIsOpen] = useState(false);
  const [statisticsIsOpen, setStatisticsIsOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<GetColab>();
  const [sortIconValue, setSortIconValue] = useState(1);
  const [displayAll, setDisplayAll] = useState(false);
  const [hasFilters, setHasFilters] = useState(false);
  const [clientFilters, setClientFilters] = useState<ClientFilters>(defaultClientFilters);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkAction, setBulkAction] = useState<'status' | 'category' | 'auto' | null>(null);
  const [modelQuickFilter, setModelQuickFilter] = useState(allModelQuickFilter);
  const detailsPanelSize = usePanelResize();

  const clientFilteredRecords = data.filter(recordMatchesClientFilters);
  const filteredRecords =
    modelQuickFilter === allModelQuickFilter
      ? clientFilteredRecords
      : clientFilteredRecords.filter(record => getCarModelQuickFilterValue(record) === modelQuickFilter);
  const pageCount = Math.max(1, Math.ceil((filteredRecords?.length || 0) / perPage));
  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedRecords = filteredRecords?.slice(startIndex, endIndex) || [];
  const selectedRecords = filteredRecords.filter(record => selectedRowKeys.includes(getRecordKey(record)));
  const pageRowKeys = paginatedRecords.map(getRecordKey);
  const isCurrentPageSelected = pageRowKeys.length > 0 && pageRowKeys.every(rowKey => selectedRowKeys.includes(rowKey));
  const isCurrentPagePartiallySelected =
    !isCurrentPageSelected && pageRowKeys.some(rowKey => selectedRowKeys.includes(rowKey));
  const categoryOptions = getCategoryOptions();

  useEffect(() => {
    const selectedModelIsAvailable =
      modelQuickFilter === allModelQuickFilter ||
      clientFilteredRecords.some(record => getCarModelQuickFilterValue(record) === modelQuickFilter);

    if (!selectedModelIsAvailable) {
      setModelQuickFilter(allModelQuickFilter);
    }
  }, [clientFilteredRecords, modelQuickFilter]);

  const [query, setQuery] = useState<ColabQuery>({
    startDate: defaultStart,
    endDate: defaultEnd,
  });

  const fillerArray = new Array(5).fill(0).map(() => Math.random());

  const onSortIconValueChange = () => {
    setSortIconValue(currentValue => {
      const nextValue = -currentValue;
      sortRecordsByDate(nextValue);
      return nextValue;
    });
  };

  async function fetchData() {
    try {
      setIsLoading(true);
      const colabs = await getColabs(query);
      const uniqueRecords = removeDuplicateRecords(colabs.entities);
      setData(uniqueRecords);
      if (displayAll) {
        setPerPage(Math.max(uniqueRecords.length, 1));
      }
    } catch (error) {
      console.error('Error occurred while fetching colab records: ', error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [query]);

  function sortRecordsByDate(value: number) {
    const sortedRecords = [...data].sort((a: GetColab, b: GetColab) => {
      return value === 1 ? b.created.localeCompare(a.created) : a.created.localeCompare(b.created);
    });

    setData(sortedRecords);
  }

  function handleOpenColabDetails(entity: GetColab) {
    setSelectedRecord(entity);
    setStatisticsIsOpen(false);
    setDetailsIsOpen(true);
  }

  function handleOpenStatistics() {
    setDetailsIsOpen(false);
    setStatisticsIsOpen(true);
  }

  function handleUpdateFilters(
    startDate: string,
    endDate: string,
    vehicleModel: string[],
    status: string[],
    category: string[],
    recordType: string,
    source: string[],
    market: string[],
    minAge: string,
    maxAge: string,
    searchText: string,
  ) {
    updatedStartDate = startDate;
    updatedEndDate = endDate;
    setClientFilters({ vehicleModel, status, category, source, market, minAge, maxAge, searchText });
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setQuery(prevQuery => ({
      ...prevQuery,
      startDate: updatedStartDate,
      endDate: updatedEndDate,
      vehicleModel: vehicleModel.length === 1 ? vehicleModel[0] : '',
      status: status,
      category: category,
      recordType: recordType,
    }));
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < pageCount) {
      handlePageChange(currentPage + 1);
    }
  };

  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(1);
  }, [currentPage, pageCount]);

  function handlePerPageSelectChange(value: string): void {
    const isAll = value === 'all';
    setCurrentPage(1);

    if (!hasFilters) {
      setDisplayAll(isAll);
      if (isAll) {
        handleUpdateFilters('', '', [], [], [], '', [], [], '', '', '');
      } else {
        handleUpdateFilters(defaultStart, defaultEnd, [], [], [], '', [], [], '', '', '');
        setPerPage(Number(value));
      }
    } else if (isAll) {
      setDisplayAll(true);
      setPerPage(Math.max(filteredRecords.length, 1));
    } else {
      setDisplayAll(false);
      setPerPage(Number(value));
    }
  }

  function getRecordKey(record: GetColab) {
    return `${record.partitionKey}:${record.rowKey}`;
  }

  function removeDuplicateRecords(records: GetColab[]) {
    const seen = new Set<string>();
    return records.filter(record => {
      const key = getRecordKey(record);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function recordMatchesClientFilters(record: GetColab) {
    const recordSearchText = Object.values(record)
      .filter(value => value !== undefined && value !== null && value !== '')
      .join(' ')
      .toLowerCase();
    const minAge = clientFilters.minAge !== '' ? Number(clientFilters.minAge) : undefined;
    const maxAge = clientFilters.maxAge !== '' ? Number(clientFilters.maxAge) : undefined;
    const age = Number(record.age);

    return (
      (clientFilters.vehicleModel.length === 0 || clientFilters.vehicleModel.includes(record.vehicleModel)) &&
      (clientFilters.status.length === 0 || clientFilters.status.includes(record.status)) &&
      (clientFilters.category.length === 0 || clientFilters.category.includes(record.category)) &&
      (clientFilters.source.length === 0 || clientFilters.source.includes(record.fromAppName)) &&
      (clientFilters.market.length === 0 || clientFilters.market.includes(record.vehicleMarket)) &&
      (minAge === undefined || age >= minAge) &&
      (maxAge === undefined || age <= maxAge) &&
      (clientFilters.searchText === '' || recordSearchText.includes(clientFilters.searchText.toLowerCase()))
    );
  }

  function toggleRowSelection(record: GetColab) {
    const rowKey = getRecordKey(record);
    setSelectedRowKeys(prev =>
      prev.includes(rowKey) ? prev.filter(selectedRowKey => selectedRowKey !== rowKey) : [...prev, rowKey],
    );
  }

  function toggleCurrentPageSelection() {
    setSelectedRowKeys(prev => {
      if (isCurrentPageSelected) return prev.filter(rowKey => !pageRowKeys.includes(rowKey));
      return Array.from(new Set([...prev, ...pageRowKeys]));
    });
  }

  function handleModelQuickFilterChange(model: string) {
    setModelQuickFilter(model);
    setCurrentPage(1);
    setSelectedRowKeys([]);
  }

  async function handleBulkStatusUpdate() {
    if (!bulkStatus || selectedRecords.length === 0) return;

    setIsBulkUpdating(true);
    setBulkAction('status');
    try {
      await Promise.all(selectedRecords.map(record => updateStatus(record.partitionKey, record.rowKey, bulkStatus)));
      setSelectedRowKeys([]);
      setBulkStatus('');
    } finally {
      setIsBulkUpdating(false);
      setBulkAction(null);
    }
  }

  async function handleBulkCategoryUpdate() {
    if (!bulkCategory || selectedRecords.length === 0) return;

    setIsBulkUpdating(true);
    setBulkAction('category');
    try {
      await Promise.all(
        selectedRecords.map(record => updateCategory(record.partitionKey, record.rowKey, bulkCategory)),
      );
      setSelectedRowKeys([]);
      setBulkCategory('');
    } finally {
      setIsBulkUpdating(false);
      setBulkAction(null);
    }
  }

  async function handleAutoCategorizeSelected() {
    if (selectedRecords.length === 0) return;

    setIsBulkUpdating(true);
    setBulkAction('auto');
    try {
      await Promise.all(
        selectedRecords.map(record => updateCategory(record.partitionKey, record.rowKey, suggestColabCategory(record))),
      );
      setSelectedRowKeys([]);
    } finally {
      setIsBulkUpdating(false);
      setBulkAction(null);
    }
  }

  function getVin(entity: GetColab) {
    return entity.vin || entity.VIN || '';
  }

  function toExcel() {
    if (filteredRecords?.length > 0) convertToExcel(filteredRecords);
  }

  const sortLabel = sortIconValue === 1 ? 'Newest first' : 'Oldest first';

  return (
    <div className="z-10 availabilities-table min-h-screen bg-white pb-16">
      {selectedRecords.length > 0 && (
        <div role="region" aria-label="Bulk edit toolbar" aria-busy={isBulkUpdating} className="colab-bulk-toolbar">
          <div className="colab-bulk-toolbar__summary">
            <span className="colab-bulk-toolbar__count">{selectedRecords.length}</span>
            <div>
              <div className="colab-bulk-toolbar__title">Bulk edit</div>
              <div className="colab-bulk-toolbar__meta">rows selected</div>
            </div>
          </div>

          <div className="colab-bulk-toolbar__actions">
            <div className="colab-bulk-toolbar__group">
              <label htmlFor="bulkStatusSelect" className="colab-bulk-toolbar__label">
                Status
              </label>
              <select
                id="bulkStatusSelect"
                className="colab-field-control colab-bulk-toolbar__select"
                value={bulkStatus}
                onChange={event => setBulkStatus(event.target.value)}
                disabled={isBulkUpdating}>
                <option value="">Choose</option>
                {recordStatuses.map(({ id, label, value }) => (
                  <option key={id} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleBulkStatusUpdate}
                disabled={!bulkStatus || isBulkUpdating}
                title={`Save status for ${selectedRecords.length} selected rows`}
                className="colab-bulk-toolbar__button colab-bulk-toolbar__button--primary">
                {bulkAction === 'status' ? (
                  <span className="colab-bulk-toolbar__spinner" aria-hidden="true" />
                ) : (
                  <Icon name="save" size="small" variant="light" padding={0} />
                )}
                <span>{bulkAction === 'status' ? 'Saving...' : 'Save status'}</span>
              </button>
            </div>

            <div className="colab-bulk-toolbar__group">
              <label htmlFor="bulkCategorySelect" className="colab-bulk-toolbar__label">
                Category
              </label>
              <ColabCategoryCombobox
                id="bulkCategorySelect"
                value={bulkCategory}
                options={categoryOptions}
                onChange={setBulkCategory}
                disabled={isBulkUpdating}
                placeholder="Choose"
                className="colab-bulk-toolbar__select colab-bulk-toolbar__select--wide"
              />
              <button
                type="button"
                onClick={handleBulkCategoryUpdate}
                disabled={!bulkCategory || isBulkUpdating}
                title={`Save category for ${selectedRecords.length} selected rows`}
                className="colab-bulk-toolbar__button colab-bulk-toolbar__button--primary">
                {bulkAction === 'category' ? (
                  <span className="colab-bulk-toolbar__spinner" aria-hidden="true" />
                ) : (
                  <Icon name="save" size="small" variant="light" padding={0} />
                )}
                <span>{bulkAction === 'category' ? 'Saving...' : 'Save category'}</span>
              </button>
            </div>

            <button
              type="button"
              onClick={handleAutoCategorizeSelected}
              disabled={isBulkUpdating}
              title={`Auto categorize and save ${selectedRecords.length} selected rows`}
              className="colab-bulk-toolbar__button colab-bulk-toolbar__button--secondary">
              {bulkAction === 'auto' ? (
                <span className="colab-bulk-toolbar__spinner colab-bulk-toolbar__spinner--dark" aria-hidden="true" />
              ) : (
                <Icon name="auto_awesome" size="small" variant="gray" padding={0} />
              )}
              <span>{bulkAction === 'auto' ? 'Categorizing...' : 'Auto categorize'}</span>
            </button>
          </div>
        </div>
      )}
      <ColabFilters
        updateFilters={handleUpdateFilters}
        colabRecords={data}
        setPerPage={setPerPage}
        setDisplayAll={setDisplayAll}
        setHasFilters={setHasFilters}
        actions={
          <Dropdown icon="more_vert" isBorderless label="">
            <DropdownItem onClick={handleOpenStatistics}>
              <div className="flex items-center justify-between gap-4">
                <span>Statistics</span>
                <Icon name="bar_chart" size="small" variant="gray" padding={0} />
              </div>
            </DropdownItem>
            <DropdownItem onClick={toExcel}>
              <div className="flex items-center justify-between gap-4">
                <span>Export to Excel</span>
                <Icon name="download" size="small" variant="gray" padding={0} />
              </div>
            </DropdownItem>
          </Dropdown>
        }
      />
      <ColabModelQuickFilter
        records={clientFilteredRecords}
        selectedModel={modelQuickFilter}
        onChange={handleModelQuickFilterChange}
      />
      <div className="px-6">
        <div className="-mx-6">
          <div className="align-middle">
            <table className="colab-table w-full table-auto">
              <caption className="sr-only">Co:Lab feedback records</caption>
              <thead className="border-b border-gray-200 bg-neutral-50">
                <tr>
                  <th scope="col" className="py-3.5 pr-3 pl-6 text-left">
                    <ColabCheckbox
                      id="selectColabCurrentPage"
                      checked={isCurrentPageSelected}
                      indeterminate={isCurrentPagePartiallySelected}
                      onChange={toggleCurrentPageSelection}
                      ariaLabel="Select current page"
                      extraClasses="colab-table-checkbox"
                    />
                  </th>
                  <th
                    scope="col"
                    aria-sort={sortIconValue === 1 ? 'descending' : 'ascending'}
                    className="py-0 pr-3 text-left">
                    <button
                      type="button"
                      onClick={onSortIconValueChange}
                      title={sortLabel}
                      className="group inline-flex items-center gap-1 py-3.5 text-sm font-medium text-neutral-400 hover:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2">
                      <span>DATE</span>
                      <Icon
                        extraClasses={classNames('transition', sortIconValue === -1 && 'rotate-180')}
                        name="arrow_upward"
                        size="small"
                        variant="gray"
                        padding={0}
                      />
                    </button>
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-neutral-400">
                    TRANSLATED FEEDBACK
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-neutral-400">
                    CATEGORY
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-neutral-400">
                    MARKET
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-neutral-400">
                    VIN
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-neutral-400">
                    CAR MODEL
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-neutral-400">
                    APP COMING FROM
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-neutral-400">
                    STATUS
                  </th>
                  <th scope="col" className="py-3.5 pr-6 pl-3 text-right">
                    <span className="sr-only">Open details</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {isLoading ? (
                  fillerArray.map(rowKey => (
                    <tr key={rowKey}>
                      {Array.from({ length: 10 }).map((_, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={classNames('px-3 py-3', cellIndex === 0 && 'pl-6', cellIndex === 9 && 'pr-6')}>
                          <LoadingMask />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedRecords.length > 0 ? (
                  paginatedRecords.map(entity => {
                    const rowKey = getRecordKey(entity);
                    const isSelected = selectedRowKeys.includes(rowKey);

                    return (
                      <tr
                        key={rowKey}
                        className={classNames('transition-colors hover:bg-teal-50/40', isSelected && 'bg-teal-50/70')}>
                        <td className="py-3 pr-3 pl-6 align-middle">
                          <ColabCheckbox
                            id={`selectColabRow-${rowKey}`}
                            checked={isSelected}
                            onChange={() => toggleRowSelection(entity)}
                            ariaLabel={`Select feedback from ${formatDateOnly(entity.created)}`}
                            extraClasses="colab-table-checkbox"
                          />
                        </td>
                        <td className="whitespace-nowrap py-3 pr-3 align-middle text-sm font-medium text-gray-900">
                          {formatDateOnly(entity.created)}
                        </td>
                        <td className="min-w-0 px-3 py-3 align-middle">
                          <div
                            title={entity.translated || undefined}
                            className={classNames(
                              'colab-table-feedback overflow-hidden text-sm leading-5',
                              entity.translated ? 'text-gray-700' : 'text-gray-400',
                            )}>
                            {entity.translated || 'No translated feedback'}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle text-sm text-gray-700">
                          <ColabCategory colabEntity={entity} compact />
                        </td>
                        <td className="px-3 py-3 align-middle text-sm text-gray-600">
                          <div className="truncate" title={entity.vehicleMarket || undefined}>
                            {entity.vehicleMarket || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle font-mono text-xs text-gray-600">
                          <div className="truncate" title={getVin(entity) || undefined}>
                            {getVin(entity) || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle text-sm text-gray-600">
                          <div className="truncate" title={getCarModelLabel(entity.vehicleModel) || undefined}>
                            {getCarModelLabel(entity.vehicleModel) || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle text-sm text-gray-600">
                          <div className="truncate" title={entity.fromAppName || undefined}>
                            {entity.fromAppName || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle text-sm text-gray-600">
                          <ColabStatusCell colabEntity={entity} compact />
                        </td>
                        <td className="py-3 pr-6 pl-3 text-right align-middle">
                          <button
                            type="button"
                            onClick={() => handleOpenColabDetails(entity)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
                            aria-label={`Open feedback from ${formatDateOnly(entity.created)}`}>
                            <Icon name="list_alt" size="small" variant="gray" padding={0} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-sm text-gray-500">
                      No Co:Lab records match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {!isLoading && filteredRecords.length > 0 && (
        <div className="mt-4 flex justify-between border-t p-6">
          <Button
            onClick={goToPreviousPage}
            extraClasses={`px-3 py-2 mx-1 rounded-md ${
              currentPage === 1 ? 'bg-gray-300 text-gray-600' : 'bg-blue-500 text-white'
            }`}
            isDisabled={currentPage === 1}
            variant="white">
            Previous Page
          </Button>
          <div>
            <label htmlFor="perPageSelect" className="text-gray-500">
              Records per page:{' '}
            </label>
            <select
              id="perPageSelect"
              className="colab-field-control colab-per-page-select"
              value={displayAll ? 'all' : perPage}
              onChange={e => handlePerPageSelectChange(e.target.value)}>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value="all">all</option>
            </select>
          </div>
          <div className="text-gray-500">
            Page {currentPage} of {pageCount}
          </div>
          <Button
            onClick={goToNextPage}
            extraClasses={`px-3 py-2 mx-1 rounded-md ${
              currentPage === pageCount ? 'bg-gray-300 text-gray-600' : 'bg-blue-500 text-white'
            }`}
            isDisabled={currentPage === pageCount}
            variant="white">
            Next Page
          </Button>
        </div>
      )}
      <SlideOver
        isOpen={detailsIsOpen}
        onCancel={() => setDetailsIsOpen(false)}
        onClose={() => setDetailsIsOpen(false)}
        size={detailsPanelSize}>
        <ColabDetails colabEntity={selectedRecord}></ColabDetails>
      </SlideOver>
      <SlideOver
        isOpen={statisticsIsOpen}
        onCancel={() => setStatisticsIsOpen(false)}
        onClose={() => setStatisticsIsOpen(false)}
        size={detailsPanelSize}>
        <ColabStatistics records={filteredRecords} totalRecords={data.length} />
      </SlideOver>
    </div>
  );
}

export default ColabsTable;
